import { z } from 'zod';
import { SLUG_PATTERN } from './patterns.js';

/**
 * External servers (ADR-0033): servers whose own sites the panel backs up and
 * watches but does not run. Their agent reports what it found on them; the
 * report crosses from the server to the panel and is checked here like any
 * other input.
 */

const IPV4 = /^(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/** Connecting an external server: a name for it and where it is. */
export const registerHostSchema = z.object({
  name: z.string().min(2).max(63).regex(SLUG_PATTERN),
  address: z.string().trim().regex(IPV4, 'An IPv4 address'),
}).strict();
export type RegisterHostRequest = z.input<typeof registerHostSchema>;

/** Where the panel asks for a server's SSH host key: the server's own address, this port. */
export const sshHostKeySchema = z.object({ port: z.number().int().min(1).max(65_535).default(22) }).strict();
export type SshHostKeyRequest = z.input<typeof sshHostKeySchema>;

/**
 * Installing an agent over SSH (ADR-0036): the login is used once, for this
 * installation, and kept nowhere. The host key's fingerprint is the one the
 * operator was shown and confirmed; a different key refuses the connection.
 */
export const sshInstallSchema = z
  .object({
    port: z.number().int().min(1).max(65_535).default(22),
    username: z.string().trim().min(1).max(64).regex(/^[a-z_][a-z0-9_.-]*$/i).default('root'),
    fingerprint: z.string().regex(/^SHA256:[A-Za-z0-9+/]{43}$/),
    password: z.string().min(1).max(1024).optional(),
    privateKey: z.string().trim().min(1).max(16_384).optional(),
    passphrase: z.string().max(1024).optional(),
  })
  .strict()
  .refine((value) => Boolean(value.password) !== Boolean(value.privateKey), { message: 'A password or a key, not both', path: ['password'] });
export type SshInstallInput = z.infer<typeof sshInstallSchema>;
export type SshInstallRequest = z.input<typeof sshInstallSchema>;

/**
 * A watched site's copy put on an external server: its own, or another one
 * standing in. Confirmed by typing the site's domain, like any restore that
 * writes over what is there.
 */
export const restoreToHostSchema = z.object({
  targetNodeId: z.string().uuid(),
  /** Point the site's names at the target afterwards, when it is another server. */
  switchDns: z.boolean().default(false),
  confirmation: z.string().min(1).max(253),
}).strict();
export type RestoreToHostRequest = z.input<typeof restoreToHostSchema>;

/** What a site on a host is, as its agent could tell from the server itself. */
export const HOSTED_SITE_KINDS = ['wordpress', 'convertstudio', 'static', 'app', 'unknown'] as const;
export type HostedSiteKind = (typeof HOSTED_SITE_KINDS)[number];

/**
 * Sites whose files are the whole site: pages a server hands out as they are,
 * whoever made them (ADR-0038). What can be copied or made from files alone.
 */
export const FILES_SITE_KINDS: ReadonlySet<string> = new Set<HostedSiteKind>(['static', 'convertstudio']);

const absolutePath = z.string().min(1).max(1024).regex(/^\/[^\0]*$/);

/** What serves a host's sites, as the host agent reads it (ADR-0043): its web servers, and Docker running WordPress (ADR-0055). */
export const WEB_SERVERS = ['nginx', 'apache', 'docker'] as const;

/** One site a host's web server serves, and what it is. */
export const discoveredSiteSchema = z.object({
  /** Which web server serves it; absent from agents older than ADR-0043, which read nginx only. */
  webServer: z.enum(WEB_SERVERS).optional(),
  names: z.array(z.string().min(1).max(253)).max(100),
  kind: z.enum(HOSTED_SITE_KINDS),
  /** The directory the web server serves, where there is one. */
  root: absolutePath.nullable(),
  /** The file its server block or virtual host is written in. */
  configFile: absolutePath.nullable(),
  /** Where it proxies to, for an application. */
  upstream: z.string().max(300).nullable(),
  /** The systemd unit behind the upstream, for an application. */
  service: z.string().max(200).nullable(),
  version: z.string().max(60).nullable(),
  /** The database a WordPress uses: its name, never its password. */
  database: z.object({ engine: z.literal('mysql'), name: z.string().max(200), host: z.string().max(300) }).nullable(),
  /** Roughly how much there is to back up, in bytes. */
  sizeBytes: z.number().int().nonnegative().nullable(),
}).strict();
export type DiscoveredSite = z.infer<typeof discoveredSiteSchema>;

export const hostDiscoverySchema = z.object({
  hostname: z.string().max(253),
  nginx: z.boolean(),
  /** Every web server found; absent from agents older than ADR-0043. */
  webServers: z.array(z.enum(WEB_SERVERS)).max(5).optional(),
  sites: z.array(discoveredSiteSchema).max(500),
  /** The ConvertStudio console on this host, when there is one: its address, for the sites it publishes. */
  convertstudio: z.object({ console: z.string().max(253).nullable(), service: z.string().max(200) }).nullable(),
  problems: z.array(z.string().max(500)).max(50),
}).strict();
export type HostDiscovery = z.infer<typeof hostDiscoverySchema>;
